import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

interface SolairePageMobileProps {
  t: any;
  isRTL: boolean;
}

const SolairePageContainer = styled.div<{ $isRTL: boolean }>`
  min-height: 100vh;
  background: #ffffff;
  padding: 1rem 1rem 0 1rem;
  padding-top: 100px;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const Container = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;

const Header = styled.div<{ $isRTL: boolean }>`
  text-align: ${props => props.$isRTL ? 'right' : 'left'};
  margin-bottom: 2rem;
  direction: ${props => props.$isRTL ? 'rtl' : 'ltr'};
`;

const MainTitle = styled.h1<{ $isRTL: boolean }>`
  font-size: 2rem;
  color: #1f2937;
  margin: 0 0 0.75rem 0;
  font-weight: 700;
  line-height: 1.2;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const Subtitle = styled.p<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #1f2937;
  margin: 0;
  font-weight: 400;
  line-height: 1.4;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const ContentSection = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
`;

const ContentGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Paragraph = styled.p<{ $isRTL: boolean }>`
  font-size: 0.95rem;
  color: #374151;
  line-height: 1.6;
  margin: 0;
  text-align: justify;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
  
  strong {
    color: #1f2937;
    font-weight: 600;
  }
`;

const HighlightText = styled.span`
  color: #dc2626;
  font-weight: 600;
`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
`;

const SolaireImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const PDFSection = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  margin-top: 2rem;
`;

const PDFSectionTitle = styled.h3<{ $isRTL: boolean }>`
  font-size: 1.3rem;
  color: #1f2937;
  margin-bottom: 1.5rem;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const PDFCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.15);
  }
`;

const PDFIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #dc2626, #ef4444);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  margin: 0 auto 1rem;
`;

const PDFCardTitle = styled.h4<{ $isRTL: boolean }>`
  font-size: 1rem;
  color: #1f2937;
  margin: 0;
  font-weight: 600;
  font-family: ${props => props.$isRTL ? 
    "'Fustat', 'Amiri', 'Noto Sans Arabic', 'Arial', sans-serif" : 
    "'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif"};
`;

const SolairePageMobile: React.FC<SolairePageMobileProps> = ({ t, isRTL }) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePDFDownload = (pdfName: string) => {
    const pdfPath = `/images/solaire/${pdfName}`;
    const link = document.createElement('a');
    link.href = pdfPath;
    link.download = pdfName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <SolairePageContainer $isRTL={isRTL}>
        <Container>
          <Header $isRTL={isRTL}>
            <MainTitle $isRTL={isRTL}>{t.solairePageTitle}</MainTitle>
            <Subtitle $isRTL={isRTL}>{t.solairePageSubtitle}</Subtitle>
          </Header>

          <ImageContainer>
            <SolaireImage 
              src="/images/solaire/solaire.jpg" 
              alt="Solutions Solaires" 
            />
          </ImageContainer>

          <ContentSection
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ContentGrid>
              <Paragraph $isRTL={isRTL}>
                {t.solairePageContent1}
              </Paragraph>

              <Paragraph $isRTL={isRTL}>
                {t.solairePageContent2}
              </Paragraph>

              <Paragraph $isRTL={isRTL}>
                {t.solairePageContent3}
              </Paragraph>

              <Paragraph $isRTL={isRTL}>
                <strong>{t.techyid}</strong> {t.solairePageContent4}
              </Paragraph>
            </ContentGrid>
          </ContentSection>

          <PDFSection>
            <PDFSectionTitle $isRTL={isRTL}>
              {t.solairePageDocumentationTitle}
            </PDFSectionTitle>
            
            <PDFCard
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handlePDFDownload('Catalogue offre solaire (2).pdf')}
            >
              <PDFIcon>📄</PDFIcon>
              <PDFCardTitle $isRTL={isRTL}>
                {t.solairePageCatalogueTitle}
              </PDFCardTitle>
            </PDFCard>
          </PDFSection>
        </Container>
      </SolairePageContainer>
      <Footer t={t} isRTL={isRTL} />
    </>
  );
};

export default SolairePageMobile;
